import { put, takeLatest, select } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import ShortId from 'shortid';
import {
  FETCH_PROGRAM_DATA_REQUEST,
  FETCH_PROGRAM_DATA_SUCCESS,
  FETCH_PROGRAM_DATA_FAILURE,
  FETCH_CONTENT_LIBRARY_REQUEST,
  FETCH_CONTENT_LIBRARY_SUCCESS,
  FETCH_CONTENT_LIBRARY_FAILURE,
  HANDLE_REMOVE_REQUEST,
  HANDLE_REMOVE_SUCCESS,
  HANDLE_REMOVE_FAILURE,
  HANDLE_DROP_REQUEST,
  HANDLE_DROP_SUCCESS,
  HANDLE_DROP_FAILURE,
  UPDATE_WORKSPACE_DATA,
  SAVE_WORK_STATE,
  RESET_CURRENT_NODE_SELECTED,
  FETCH_EVENT_DATA_REQUEST,
  FETCH_EVENT_DATA_SUCCESS,
  FETCH_EVENT_DATA_FAILURE,
  CREATE_EVENT_SUCCESS,
  CREATE_EVENT_FAILURE,
  CREATE_EVENT_REQUEST,
  UPDATE_EVENT_REQUEST,
  DELETE_EVENT_REQUEST,
  UPDATE_EVENT_SUCCESS,
  UPDATE_EVENT_FAILURE,
  DELETE_EVENT_SUCCESS,
  UNDO_REQUEST,
  UNDO_WORK_STATE,
  REDO_REQUEST,
  REDO_WORK_STATE,
  SAVE_PROGRAM_REQUEST,
  SAVE_PROGRAM_SUCCESS,
  SAVE_PROGRAM_FAILURE,
  FETCH_BROADCAST_DATA_REQUEST,
  FETCH_BROADCAST_DATA_FAILURE,
  FETCH_BROADCAST_DATA_SUCCESS,
  SAVE_BROADCAST_SUCCESS,
  SAVE_BROADCAST_FAILURE,
  SAVE_BROADCAST_REQUEST,
  SEARCH_CONTENT_REQUEST,
  SEARCH_CONTENT_SUCCESS,
  SEARCH_CONTENT_FAILURE,
  UPDATE_CONTENT_REQUEST,
  UPDATE_CONTENT_SUCCESS,
  UPDATE_CONTENT_FAILURE,
  FETCH_DESKTOP_PLAYER_REQUEST,
  FETCH_DESKTOP_PLAYER_SUCCESS,
  FETCH_DESKTOP_PLAYER_FAILURE,
  HANDLE_CHANGE_PROPERTIES,
  FETCH_AUDIENCES_SUCCESS,
  FETCH_AUDIENCES_FAILURE,
  FETCH_AUDIENCES_REQUEST,
  FETCH_AUTOMATED_BROADCAST_REQUEST,
  FETCH_AUTOMATED_BROADCAST_SUCCESS,
  FETCH_AUTOMATED_BROADCAST_FAILURE,
  FETCH_TENANT_SETTINGS_REQUEST,
  FETCH_TENANT_SETTINGS_SUCCESS,
  FETCH_TENANT_SETTINGS_FAILURE
} from './constants';

import {
  getProgram,
  getContentLibrary,
  getEventData,
  createEvent,
  updateEvent,
  saveProgram,
  deleteEvent,
  getBroadcast,
  saveBroadcast,
  searchContent,
  getContentDetail,
  getDesktopPlayerData,
  getAudiences,
  getAutomatedBroadcasts,
  updateBroadcast,
  getAutomatedBroadcast,
  saveAutomatedBroadcast,
  updateAutomatedBroadcast, getTenantSettings
} from './api';
import {
  CONTENT_COMPONENT, CTA_ACTIVATE_BROADCAST,
  CTA_COMPONENT,
  CTA_JOIN_SEGMENT,
  CTA_OTHER_CONTENT,
  CTA_OWN_ANCHOR,
  CTA_TRIGGER_EVENT,
  NODE_CTA,
  NODE_END_OF_CONTENT,
  NODE_INIT,
  NORMAL,
  OUT_OF_DATE,
  ROOT_NODE,
  UP_TO_DATE
} from '../../constants/ItemTypes';
import * as CTATypes from '../../constants/CTATypes';
import { ICON_CTA_NOTIFICATION } from '../../constants/ImagesUrl';
import { AUTOMATED_MODE, BROADCAST_MODE, PROGRAM_MODE } from '../../constants/EditorMode';

// Select program state
const programState = state => state.program;

/**
 * Save program/broadcast
 */
function* saveWorkspace() {
  try {
    // Get program state
    const state = yield select(programState);
    const editorMode = state.get('editorMode');
    // Call api save program/broadcast
    switch (editorMode) {
      case PROGRAM_MODE:
        yield put({ type: SAVE_PROGRAM_REQUEST });
        break;
      case BROADCAST_MODE:
      case AUTOMATED_MODE:
        yield put({ type: SAVE_BROADCAST_REQUEST });
        break;
      default:
        break;
    }
  } catch (e) {
    console.log(e.response || e);
  }
}

// Sort CTA by fromTime
const sortCTA = (data) => {
  const modifiedData = { ...data };

  // Sort CTA by fromTime
  for (let eventIndex = 0; eventIndex < modifiedData.events.length; eventIndex += 1) {
    const { contents } = modifiedData.events[eventIndex];
    for (let contentIndex = 0; contentIndex < contents.length; contentIndex += 1) {
      modifiedData.events[eventIndex].contents[contentIndex].ctas
        .sort((a, b) => {
          if (a.fromTime < b.fromTime) { return -1; }
          if (a.fromTime > b.fromTime) { return 1; }
          return 0;
        });
    }
  }
  return modifiedData;
};

// Standardize CTAs
const standardizeCTAs = (ctas) => {
  // Sort by ctaId (string)
  const result = ctas.sort((a, b) => a.id.localeCompare(b.id));
  return result.map(res => ({
    id: res.id,
    name: res.name,
    image: res.image,
    fromTime: res.fromTime,
    toTime: res.toTime,
    pause: res.pause,
    position: res.position,
    type: res.type,
    size: res.size,
    text: res.text,
    textColor: res.textColor,
    textSize: res.textSize,
    textStyle: res.textStyle,
    backgroundColor: res.backgroundColor,
    animation: res.animation
  }));
};

// Compare content
const isUpdated = (oldContent, newContent) => {
  // Check content was deleted => return false
  if (newContent.error) {
    return false;
  }
  // Don't check type 'notification'
  if (newContent.type === 'notification') {
    return true;
  }
  const contentA = {
    id: oldContent.contentId,
    type: oldContent.type,
    thumbnail: oldContent.thumbnail,
    duration: oldContent.duration,
    name: oldContent.name,
    label: oldContent.label,
    stream: oldContent.stream,
    ctas: standardizeCTAs(oldContent.ctas)
  };
  const contentB = {
    id: newContent.id,
    type: newContent.type,
    thumbnail: newContent.thumbnail,
    duration: newContent.duration,
    name: newContent.name,
    label: newContent.label,
    stream: newContent.stream,
    ctas: standardizeCTAs(newContent.ctas)
  };
  return JSON.stringify(contentA) === JSON.stringify(contentB);
};

// Push update status
const pushUpdateStatus = async (data) => {
  try {
    const newWorkspaceData = { ...data };
    // List all content ID on workspace
    let listContentId = [];
    data.events.forEach((event) => {
      event.contents.forEach((content) => {
        listContentId.push(content.contentId);
      });
    });
    // Unique
    listContentId = listContentId.filter((v, i, a) => a.indexOf(v) === i);
    // Get latest details of all content in list
    let results = await Promise
      .all(listContentId.map(id => getContentDetail(id, localStorage.getItem('token'))));
    results = results.map((res) => {
      if (res.status === 200) {
        return res.data;
      }
      return {
        id: res.idError,
        error: true,
        message: 'Content not found!'
      };
    });
    // Push update status
    for (let eventIndex = 0; eventIndex < newWorkspaceData.events.length; eventIndex += 1) {
      const { contents } = newWorkspaceData.events[eventIndex];
      for (let contentIndex = 0; contentIndex < contents.length; contentIndex += 1) {
        const newContent = results.find(res => res.id === contents[contentIndex].contentId);
        // Add type, duration, name ... to all content in workspaceData
        newWorkspaceData.events[eventIndex].contents[contentIndex].type = newContent.type;
        newWorkspaceData.events[eventIndex].contents[contentIndex].contentName = newContent.name;
        // newWorkspaceData.events[eventIndex].contents[contentIndex].duration = newContent.duration;
        newWorkspaceData.events[eventIndex].contents[contentIndex].anchors = newContent.anchors;
        newWorkspaceData.events[eventIndex].contents[contentIndex]
          .contentQuality = newContent.contentQuality;
        // Add updateStatus and isDeleted
        if (isUpdated(contents[contentIndex], newContent)) {
          newWorkspaceData.events[eventIndex].contents[contentIndex].updateStatus = NORMAL;
        } else {
          newWorkspaceData.events[eventIndex].contents[contentIndex].updateStatus = OUT_OF_DATE;
          // Set isDeleted status
          if (newContent.error) {
            newWorkspaceData.events[eventIndex].contents[contentIndex].isDeleted = true;
          }
        }
      }
    }
    return newWorkspaceData;
  } catch (e) {
    console.log(e);
    throw new Error('Can not push update status to content');
  }
};

/**
 * Fetch program data
 * @param action
 */
function* fetchProgramData(action) {
  try {
    const programData = yield getProgram(action.payload.id, action.payload.token);
    // Modify workspace data
    let workspaceData = { ...programData };
    // Push update status
    workspaceData = yield pushUpdateStatus(workspaceData);
    // Sort CTA
    workspaceData = sortCTA(workspaceData);

    yield put({
      type: FETCH_PROGRAM_DATA_SUCCESS,
      workspaceData
    });
    yield put({ type: UPDATE_WORKSPACE_DATA });
    yield put({ type: SAVE_WORK_STATE });
  } catch (e) {
    console.log(e.response || e);
    yield put({
      type: FETCH_PROGRAM_DATA_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Fetch broadcast data
 * @param action
 */
function* fetchBroadcastData(action) {
  try {
    let broadcastData = {};
    switch (window.sqoonyEditorMode) {
      case BROADCAST_MODE:
        broadcastData = yield getBroadcast(action.payload.id, action.payload.token);
        break;
      case AUTOMATED_MODE:
        broadcastData = yield getAutomatedBroadcast(action.payload.id, action.payload.token);
        break;
      default:
        broadcastData = yield getBroadcast(action.payload.id, action.payload.token);
        break;
    }
    // Modify broadcastData
    let workspaceData = Object.assign({}, broadcastData, {
      events: [{
        id: broadcastData.id,
        name: broadcastData.name,
        contents: broadcastData.contents || []
      }]
    });
    delete workspaceData.contents;

    // Push update status
    workspaceData = yield pushUpdateStatus(workspaceData);
    // Sort CTA
    workspaceData = sortCTA(workspaceData);

    yield put({ type: FETCH_BROADCAST_DATA_SUCCESS, workspaceData });
    yield put({ type: UPDATE_WORKSPACE_DATA });
    yield put({ type: SAVE_WORK_STATE });
  } catch (e) {
    console.log(e.response);
    yield put({
      type: FETCH_BROADCAST_DATA_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Fetch content library
 * @param action
 */
function* fetchContentLibrary(action) {
  try {
    const contentLibrary = yield getContentLibrary(action.payload.token);
    yield put({
      type: FETCH_CONTENT_LIBRARY_SUCCESS,
      contentLibrary
    });

    // yield put({ type: SAVE_WORK_STATE });
  } catch (e) {
    console.log(e.response);
    yield put({
      type: FETCH_CONTENT_LIBRARY_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Save program
 */
function* saveProgramData() {
  try {
    // Get program state
    const state = yield select(programState);
    const programData = state.get('workspaceData').toJS();

    // Call API update program
    yield saveProgram(localStorage.getItem('token'), programData);
    // Put success
    yield put({ type: SAVE_PROGRAM_SUCCESS });
  } catch (e) {
    console.log(e.response);
    yield put({
      type: SAVE_PROGRAM_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Save broadcast
 */
function* saveBroadcastData() {
  try {
    // Get program state
    const state = yield select(programState);
    const broadcastData = state.get('workspaceData').toJS();
    broadcastData.contents = broadcastData.events[0].contents;
    delete broadcastData.events;
    // Call API update program
    switch (window.sqoonyEditorMode) {
      case BROADCAST_MODE:
        yield saveBroadcast(localStorage.getItem('token'), broadcastData);
        break;
      case AUTOMATED_MODE:
        yield saveAutomatedBroadcast(localStorage.getItem('token'), broadcastData);
        break;
      default:
        break;
    }
    // Put success
    yield put({ type: SAVE_BROADCAST_SUCCESS });
  } catch (e) {
    console.log(e.response);
    yield put({
      type: SAVE_BROADCAST_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Fetch event data
 * @param action
 */
function* fetchEventData(action) {
  try {
    const eventData = yield getEventData(action.payload.token);
    yield put({
      type: FETCH_EVENT_DATA_SUCCESS,
      eventData
    });
  } catch (e) {
    console.log(e.response);
    yield put({
      type: FETCH_EVENT_DATA_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Create new event
 * @param action
 */
function* createNewEvent(action) {
  try {
    const eventData = yield createEvent(action.payload.token, action.payload.eventName);
    yield put({
      type: CREATE_EVENT_SUCCESS,
      eventData
    });
    yield put({ type: UPDATE_WORKSPACE_DATA });
    yield put({ type: SAVE_WORK_STATE });
    // Call api save data
    yield saveWorkspace();
  } catch (e) {
    console.log('Error', e.response);
    yield put({
      type: CREATE_EVENT_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Update event data
 * @param action
 */
function* updateEventData(action) {
  try {
    switch (action.payload.editorMode) {
      case PROGRAM_MODE:
        yield updateEvent(action.payload.token, action.payload.eventId, action.payload.newName);
        break;
      case BROADCAST_MODE:
      case AUTOMATED_MODE: {
        // Get program state
        const state = yield select(programState);
        const broadcastData = state.get('workspaceData').toJS();
        broadcastData.contents = broadcastData.events[0].contents;
        broadcastData.title = action.payload.newName;
        broadcastData.name = action.payload.newName;
        delete broadcastData.events;
        // Call API update program
        if (action.payload.editorMode === BROADCAST_MODE) {
          yield updateBroadcast(localStorage.getItem('token'), broadcastData);
        } else {
          yield updateAutomatedBroadcast(localStorage.getItem('token'), broadcastData);
        }
        break;
      }
      default:
        return;
    }
    yield put({
      type: UPDATE_EVENT_SUCCESS,
      payload: action.payload
    });
    // yield put({ type: UPDATE_WORKSPACE_DATA });
    yield put({ type: SAVE_WORK_STATE });
    // Call api save data
    // yield saveWorkspace();
  } catch (e) {
    console.log('Error', e.response || e);
    yield put({
      type: UPDATE_EVENT_FAILURE,
      appError: {
        status: e.response ? e.response.status : null,
        statusText: e.response ? e.response.statusText : '',
        data: {
          message: e.response ? e.response.data.message : e.message
        }
      }
    });
  }
}

/**
 * Delete event
 * @param action
 */
function* deleteEventData(action) {
  try {
    yield deleteEvent(action.payload.token, action.payload.eventId);
    yield put({
      type: DELETE_EVENT_SUCCESS,
      payload: action.payload
    });
    yield put({ type: UPDATE_WORKSPACE_DATA });
    yield put({ type: SAVE_WORK_STATE });
    // Call api save data
    yield saveWorkspace();
  } catch (e) {
    console.log('Error', e.response);
    yield put({
      type: DELETE_EVENT_SUCCESS,
      payload: action.payload
    });
    yield put({ type: UPDATE_WORKSPACE_DATA });
    yield put({ type: SAVE_WORK_STATE });
    // No need show error if event does not exists
    // yield put({
    //   type: DELETE_EVENT_FAILURE,
    //   appError: {
    //     status: e.response.status,
    //     statusText: e.response.statusText,
    //     data: e.response.data
    //   }
    // });
  }
}

/**
 * Find index of all node need to remove
 * @param arrContents
 * @param contentId
 */
const getRemoveIndex = (contentId, arrContents) => {
  const arrIndex = [];
  const index = arrContents.findIndex(content => content.id === contentId);
  if (index > -1) {
    arrIndex.push(index);
    if (
      arrContents[index].endOfContent &&
      arrContents[index].endOfContent.action === CTATypes.OTHER_CONTENT
    ) {
      arrIndex.push(...getRemoveIndex(arrContents[index].endOfContent.content.id, arrContents));
    }

    arrContents[index].ctas.map((cta) => {
      if (cta.linkTo && cta.linkTo.action === CTATypes.OTHER_CONTENT) {
        arrIndex.push(...getRemoveIndex(cta.linkTo.content.id, arrContents));
      }
      return true;
    });
  }
  return arrIndex;
};

/**
 * Remove Content
 * @param contentId
 * @param arrContents
 * @param positionParent
 * @returns {*}
 */
const removeContent = (contentId, arrContents, positionParent) => {
  const contents = arrContents;
  const arrIndex = getRemoveIndex(contentId, arrContents).sort((a, b) => a - b);

  // Remove linkTo or endOfContent in parent node
  if (arrIndex.length > 0) {
    if (positionParent && (positionParent.length === 2)) {
      delete contents[positionParent[1]].endOfContent;
    } else if (positionParent && (positionParent.length === 3)) {
      delete contents[positionParent[1]].ctas[positionParent[2]].linkTo;
    }
  }

  // Remove content node
  for (let i = arrIndex.length - 1; i >= 0; i -= 1) {
    contents.splice(arrIndex[i], 1);
  }

  return contents;
};

/**
 * Handle remove
 * @param action
 */
function* handleRemoveNode(action) {
  // Get program state
  const state = yield select(programState);
  // Get event index
  const eventIndex = action.payload.item.positionNode[0];
  const newEvents = state.getIn(['workspaceData', 'events']).toJS();
  // Get new contents array after remove
  let newContents = newEvents[eventIndex].contents;

  try {
    if (action.payload.itemType === CTA_OTHER_CONTENT) {
      newContents = removeContent(
        action.payload.item.id,
        newContents,
        action.payload.item.positionParent
      );
      newEvents[eventIndex].contents = newContents;
    } else if (action.payload.nodeType === NODE_END_OF_CONTENT) {
      delete newContents[action.payload.item.positionParent[1]].endOfContent;
      newEvents[eventIndex].contents = newContents;
    } else if (action.payload.nodeType === ROOT_NODE) {
      newEvents.splice(eventIndex, 1);
      yield deleteEvent(action.payload.token, action.payload.item.id);
    } else {
      delete newContents[action.payload.item.positionNode[1]]
        .ctas[action.payload.item.positionNode[2]]
        .linkTo;
      newEvents[eventIndex].contents = newContents;
    }
  } catch (e) {
    console.log(e);
    yield put({
      type: HANDLE_REMOVE_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }

  // Reset currentNodeSelected
  yield put({ type: RESET_CURRENT_NODE_SELECTED });

  yield put({
    type: HANDLE_REMOVE_SUCCESS,
    newEvents
  });
  yield put({ type: UPDATE_WORKSPACE_DATA });
  yield put({ type: SAVE_WORK_STATE });
  // Call api save data
  yield saveWorkspace();
}

/**
 * Get list Contents Id follow node
 * @param contentId
 * @param arrContents
 * @returns {Array}
 */
const getListContentIdFollow = (contentId, arrContents) => {
  const listContentId = [];
  const index = arrContents.findIndex(content => content.id === contentId);
  if (index > -1) {
    listContentId.push(contentId);
    if (
      arrContents[index].endOfContent &&
      arrContents[index].endOfContent.action === CTATypes.OTHER_CONTENT
    ) {
      listContentId
        .push(...getListContentIdFollow(arrContents[index].endOfContent.content.id, arrContents));
    }

    arrContents[index].ctas.map((cta) => {
      if (cta.linkTo && cta.linkTo.action === CTATypes.OTHER_CONTENT) {
        listContentId
          .push(...getListContentIdFollow(cta.linkTo.content.id, arrContents));
      }
      return true;
    });
  } else {
    console.log('[getListContentIdFollow] Error: content not found');
  }
  return listContentId;
};

/**
 * Validate node before drop
 * If contentId already exists => return false
 * @param source
 * @param target
 * @param events
 * @returns {boolean}
 */
const isValidSource = (source, target, events) => {
  // CTA is always allowed
  if (source.itemType !== CTA_OTHER_CONTENT) {
    return true;
  }

  if (source.item.id === target.item.id) {
    return false;
  }

  const indexTargetEvent = target.item.positionParent[0];
  if (source.sourceType) {
    // Find index of content in Tree Node
    const index = events[indexTargetEvent].contents
      .findIndex(content => content.id === source.item.id);
    if (index > -1) {
      return false;
    }
  } else {
    const indexSourceEvent = source.item.positionParent[0];

    if (indexTargetEvent !== indexSourceEvent) {
      // Get list [SOURCE] content id follow node
      const listSourceContentId =
        getListContentIdFollow(source.item.id, events[indexSourceEvent].contents);

      // Get intersection between listSourceContentId and [TARGET] contents
      const intersection = events[indexTargetEvent].contents
        .filter(content => listSourceContentId.includes(content.id));

      if (intersection.length > 0) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Handle drop (SOURCE to TARGET)
 * @param action
 */
function* handleDrop(action) {
  try {
    // Get program state
    const state = yield select(programState);
    const events = state.getIn(['workspaceData', 'events']).toJS();
    // Endpoint
    const newEvents = [...events];
    const { source, target } = action.payload;

    // Get target parent's anchors
    let parentAnchors = null;
    if (source.itemType === CTA_OWN_ANCHOR) {
      parentAnchors = events[target.item.positionParent[0]]
        .contents[target.item.positionParent[1]].anchors;
    }

    // Get source anchors
    let sourceAnchors = null;
    if (source.item.anchors && source.item.anchors.length) {
      sourceAnchors = source.item.anchors;
    }

    // Checking node is valid to drop
    if (isValidSource(source, target, events)) {
      switch (source.sourceType) {
        // CTA COMPONENT
        case CTA_COMPONENT: {
          // Refresh list events if drop Trigger_event CTA
          if (source.itemType === CTA_TRIGGER_EVENT) {
            const latestEvents = yield getEventData(localStorage.getItem('token'));
            yield put({
              type: FETCH_EVENT_DATA_SUCCESS,
              eventData: latestEvents
            });
          }
          // Refresh list segments if drop JoinAudience CTA
          if (source.itemType === CTA_JOIN_SEGMENT) {
            const latestAudiences = yield getAudiences(localStorage.getItem('token'));
            yield put({
              type: FETCH_AUDIENCES_SUCCESS,
              audiences: latestAudiences
            });
          }
          // Refresh list automated broadcast if drop Activate Automated Broadcast CTA
          if (source.itemType === CTA_ACTIVATE_BROADCAST) {
            const latestAutomatedBroadcasts = yield getAutomatedBroadcasts(localStorage.getItem('token'));
            yield put({
              type: FETCH_AUTOMATED_BROADCAST_SUCCESS,
              automatedBroadcasts: latestAutomatedBroadcasts
            });
          }
          // Remove follow node in Target event
          if (target.itemType === CTA_OTHER_CONTENT) {
            const indexTargetEvent = target.item.positionParent[0];
            const { contents: targetContents } = events[indexTargetEvent];
            const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
            const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
            for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
              newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
            }
          }
          // Set linkTo value
          let linkTo;
          switch (source.item.itemType) {
            case CTA_OWN_ANCHOR:
              linkTo = {
                action: CTATypes.OWN_ANCHOR,
                ownAnchor: parentAnchors ? parentAnchors[0].id : null
              };
              break;
            case CTA_TRIGGER_EVENT:
              linkTo = {
                action: CTATypes.TRIGGER_EVENT,
                eventId: state.getIn(['eventData', 0, 'id']) || ''
              };
              break;
            case CTA_JOIN_SEGMENT:
              linkTo = {
                action: CTATypes.JOIN_SEGMENT,
                segment: ''
              };
              break;
            default:
              linkTo = source.item.linkTo; // eslint-disable-line prefer-destructuring
              break;
          }
          // Check and add linkTo to Target
          switch (target.nodeType) {
            // Target: nodeType = NODE_END_OF_CONTENT
            case NODE_END_OF_CONTENT: {
              newEvents[target.item.positionParent[0]]
                .contents[target.item.positionParent[1]]
                .endOfContent = linkTo;
              break;
            }
            // Target: nodeType = NODE_CTA
            default:
              switch (target.itemType) {
                // Target: itemType = CTA_OTHER_CONTENT
                case CTA_OTHER_CONTENT:
                  newEvents[target.item.positionParent[0]]
                    .contents[target.item.positionParent[1]]
                    .ctas[target.item.positionParent[2]]
                    .linkTo = linkTo;
                  break;
                // Target: itemType = OTHER
                default:
                  newEvents[target.item.positionNode[0]]
                    .contents[target.item.positionNode[1]]
                    .ctas[target.item.positionNode[2]]
                    .linkTo = linkTo;
                  break;
              }
              break;
          }
          break;
        }
        // CONTENT COMPONENT
        case CONTENT_COMPONENT: {
          // Get latest content detail
          let latestContent = yield getContentDetail(source.item.id, localStorage.getItem('token'));
          // Check error and throw
          if (latestContent.status !== 200) {
            throw new Error('We can\'t find the content. Did someone delete it? Please try again.');
          } else {
            latestContent = latestContent.data;
          }
          // List CTAs of content
          const contentCTA = latestContent.ctas || [];
          // Sort CTA by fromTime
          contentCTA.sort((a, b) => {
            if (a.fromTime < b.fromTime) { return -1; }
            if (a.fromTime > b.fromTime) { return 1; }
            return 0;
          });
          // Remove follow node in Target event
          if (target.itemType === CTA_OTHER_CONTENT) {
            const indexTargetEvent = target.item.positionParent[0];
            const { contents: targetContents } = events[indexTargetEvent];
            const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
            const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
            for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
              newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
            }
          }
          // Generate a random Id
          const randomId = ShortId.generate();
          // Set node dropped id
          window.sqoonyNodeDroppedId = randomId;

          switch (target.nodeType) {
            // Target: nodeType = NODE_INIT
            case NODE_INIT:
              newEvents[target.item.positionParent[0]].contents = [];
              newEvents[target.item.positionParent[0]].contents.push({
                id: randomId,
                contentId: latestContent.id,
                contentName: latestContent.name,
                name: latestContent.name,
                label: latestContent.label,
                stream: latestContent.stream,
                duration: latestContent.duration,
                anchors: latestContent.anchors,
                availableAt: 'afterEvent',
                availableAtDetails: {
                  id: events[target.item.positionParent[0]].id,
                  availableType: 'relative',
                  time: 0
                },
                thumbnail: latestContent.type === 'notification'
                  ? ICON_CTA_NOTIFICATION
                  : latestContent.thumbnail,
                endOfContent: null,
                type: latestContent.type,
                ctas: contentCTA,
                contentQuality: latestContent.contentQuality
              });
              break;
            // Target: nodeType = NODE_END_OF_CONTENT
            case NODE_END_OF_CONTENT:
              newEvents[target.item.positionParent[0]]
                .contents[target.item.positionParent[1]]
                .endOfContent = {
                  action: CTATypes.OTHER_CONTENT,
                  content: {
                    id: randomId,
                    anchor: sourceAnchors ? sourceAnchors[0].id : null
                  }
                };
              newEvents[target.item.positionParent[0]]
                .contents.push({
                  id: randomId,
                  contentId: latestContent.id,
                  contentName: latestContent.name,
                  name: latestContent.name,
                  label: latestContent.label,
                  stream: latestContent.stream,
                  duration: latestContent.duration,
                  anchors: latestContent.anchors,
                  availableAt: 'afterContent',
                  availableAtDetails: {
                    id: newEvents[target.item.positionParent[0]]
                      .contents[target.item.positionParent[1]].id,
                    availableType: 'relative',
                    time: 0
                  },
                  thumbnail: latestContent.type === 'notification'
                    ? ICON_CTA_NOTIFICATION
                    : latestContent.thumbnail,
                  endOfContent: null,
                  type: latestContent.type,
                  ctas: contentCTA,
                  contentQuality: latestContent.contentQuality
                });
              break;
            // Target: nodeType = NODE_CTA
            default:
              switch (target.itemType) {
                // Target: itemType = CTA_OTHER_CONTENT
                case CTA_OTHER_CONTENT:
                  newEvents[target.item.positionParent[0]]
                    .contents[target.item.positionParent[1]]
                    .ctas[target.item.positionParent[2]]
                    .linkTo = {
                      action: CTATypes.OTHER_CONTENT,
                      content: {
                        id: randomId,
                        anchor: sourceAnchors ? sourceAnchors[0].id : null
                      }
                    };
                  newEvents[target.item.positionParent[0]]
                    .contents.push({
                      id: randomId,
                      contentId: latestContent.id,
                      contentName: latestContent.name,
                      name: latestContent.name,
                      label: latestContent.label,
                      stream: latestContent.stream,
                      duration: latestContent.duration,
                      anchors: latestContent.anchors,
                      availableAt: 'afterCTA',
                      availableAtDetails: {
                        id: newEvents[target.item.positionParent[0]]
                          .contents[target.item.positionParent[1]].id,
                        availableType: 'relative',
                        time: 0
                      },
                      thumbnail: latestContent.type === 'notification'
                        ? ICON_CTA_NOTIFICATION
                        : latestContent.thumbnail,
                      endOfContent: null,
                      type: latestContent.type,
                      ctas: contentCTA,
                      contentQuality: latestContent.contentQuality
                    });
                  break;
                // Target: itemType = OTHER
                default:
                  newEvents[target.item.positionNode[0]]
                    .contents[target.item.positionNode[1]]
                    .ctas[target.item.positionNode[2]]
                    .linkTo = {
                      action: CTATypes.OTHER_CONTENT,
                      content: {
                        id: randomId,
                        anchor: sourceAnchors ? sourceAnchors[0].id : null
                      }
                    };
                  newEvents[target.item.positionParent[0]]
                    .contents.push({
                      id: randomId,
                      contentId: latestContent.id,
                      contentName: latestContent.name,
                      name: latestContent.name,
                      label: latestContent.label,
                      stream: latestContent.stream,
                      duration: latestContent.duration,
                      anchors: latestContent.anchors,
                      availableAt: 'afterCTA',
                      availableAtDetails: {
                        id: newEvents[target.item.positionParent[0]]
                          .contents[target.item.positionParent[1]].id,
                        availableType: 'relative',
                        time: 0
                      },
                      thumbnail: latestContent.type === 'notification'
                        ? ICON_CTA_NOTIFICATION
                        : latestContent.thumbnail,
                      endOfContent: null,
                      type: latestContent.type,
                      ctas: contentCTA,
                      contentQuality: latestContent.contentQuality
                    });
                  break;
              }
              break;
          }
          break;
        }
        // NODE
        default: {
          const indexSourceEvent = source.item.positionParent[0];
          const indexTargetEvent = target.item.positionParent[0];
          switch (target.nodeType) {
            // Target: nodeType = NODE_INIT
            case NODE_INIT: {
              const linkedContents = [];
              const { contents } = events[indexSourceEvent];
              // Find all contents follow
              const arrIndex = getRemoveIndex(source.item.id, contents);
              // Push content to linkedContents
              arrIndex.map(index => linkedContents.push(contents[index]));

              // Check if user drop to difference event
              if (indexSourceEvent !== indexTargetEvent) {
                // Set 'afterEvent'
                linkedContents[0].availableAt = 'afterEvent';
                // Set 'availableAtDetails' ID
                linkedContents[0].availableAtDetails.id = events[target.item.positionParent[0]].id;
                // Replace event's contents with linkedContents
                newEvents[indexTargetEvent].contents = linkedContents;
                // Remove linkTo/endOfContent in Source event
                switch (source.nodeType) {
                  // [Sub] Source: NODE_INIT
                  case NODE_INIT:
                    break;
                  // [Sub] Source: NODE_END_OF_CONTENT
                  case NODE_END_OF_CONTENT:
                    newEvents[indexSourceEvent]
                      .contents[source.item.positionParent[1]]
                      .endOfContent = null;
                    break;
                  // [Sub] Source: NODE_CTA
                  default:
                    newEvents[indexSourceEvent]
                      .contents[source.item.positionParent[1]]
                      .ctas[source.item.positionParent[2]]
                      .linkTo = null;
                    break;
                }
                // Remove linkedContents in Source event
                const arrIndexSorted = arrIndex.sort((a, b) => a - b);
                for (let i = arrIndexSorted.length - 1; i >= 0; i -= 1) {
                  newEvents[indexSourceEvent].contents.splice(arrIndexSorted[i], 1);
                }
              } else {
                // Set 'afterEvent'
                linkedContents[0].availableAt = 'afterEvent';
                linkedContents[0].availableAtDetails = {
                  id: events[target.item.positionParent[0]].id,
                  availableType: 'relative',
                  time: 0
                };
                // Replace event's contents with linkedContents
                newEvents[indexSourceEvent].contents = linkedContents;
              }
              break;
            }
            // Target: nodeType = NODE_END_OF_CONTENT
            case NODE_END_OF_CONTENT: {
              switch (source.nodeType) {
                // Source: nodeType = NODE_INIT
                case NODE_INIT: {
                  // Todo: It should be removed when handling disable node successed
                  if (indexSourceEvent !== indexTargetEvent) {
                    const linkedContents = events[indexSourceEvent].contents;
                    // Set 'availableAtDetails' ID
                    linkedContents[0].availableAtDetails.id =
                      newEvents[target.item.positionParent[0]]
                        .contents[target.item.positionParent[1]].id;
                    // Set 'afterContent' for the first content
                    linkedContents[0].availableAt = 'afterContent';
                    // Add linkedContents to Target event
                    newEvents[indexTargetEvent].contents.push(...linkedContents);
                    // Set endOfContent value in Target node
                    newEvents[target.item.positionParent[0]]
                      .contents[target.item.positionParent[1]]
                      .endOfContent = {
                        action: CTATypes.OTHER_CONTENT,
                        content: {
                          id: source.item.id,
                          anchor: sourceAnchors ? sourceAnchors[0].id : null
                        }
                      };
                    // Remove all content in Source event
                    newEvents[indexSourceEvent].contents = [];
                    // Remove follow node in Target event
                    if (target.itemType === CTA_OTHER_CONTENT) {
                      const { contents: targetContents } = events[indexTargetEvent];
                      const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
                      const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
                      for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
                        newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
                      }
                    }
                  }
                  break;
                }
                // Source: nodeType = NODE_END_OF_CONTENT
                case NODE_END_OF_CONTENT: {
                  switch (source.itemType) {
                    // Source: itemType = CTA_OTHER_CONTENT
                    case CTA_OTHER_CONTENT: {
                      // Set fields
                      newEvents[target.item.positionParent[0]]
                        .contents[target.item.positionParent[1]]
                        .endOfContent = {
                          action: CTATypes.OTHER_CONTENT,
                          content: {
                            id: source.item.id,
                            anchor: newEvents[source.item.positionParent[0]].contents[source.item.positionParent[1]].endOfContent.content.anchor || ''
                          }
                        };
                      newEvents[source.item.positionParent[0]]
                        .contents[source.item.positionParent[1]]
                        .endOfContent = null;
                      // Check if drop to difference event
                      if (indexSourceEvent !== indexTargetEvent) {
                        const linkedContents = [];
                        const { contents } = events[indexSourceEvent];
                        // Find all contents follow
                        const arrIndex = getRemoveIndex(source.item.id, contents);
                        // Push content to linkedContents
                        arrIndex.map(index => linkedContents.push(contents[index]));
                        // Set 'availableAtDetails' ID
                        linkedContents[0].availableAtDetails.id =
                          newEvents[target.item.positionParent[0]]
                            .contents[target.item.positionParent[1]].id;

                        newEvents[indexTargetEvent].contents.push(...linkedContents);
                        // Remove source follow node
                        const arrIndexSorted = arrIndex.sort((a, b) => a - b);
                        for (let i = arrIndexSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexSourceEvent].contents.splice(arrIndexSorted[i], 1);
                        }
                      }
                      // Remove follow node in Target event
                      if (target.itemType === CTA_OTHER_CONTENT) {
                        const { contents: targetContents } = events[indexTargetEvent];
                        const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
                        const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
                        for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
                        }
                      }
                      break;
                    }
                    // Source: itemType = CTA
                    default:
                      newEvents[source.item.positionParent[0]]
                        .contents[source.item.positionParent[1]]
                        .endOfContent = null;
                      // Check and reset CTA_OWN_ANCHOR
                      if (source.itemType === CTA_OWN_ANCHOR) {
                        newEvents[target.item.positionParent[0]]
                          .contents[target.item.positionParent[1]]
                          .endOfContent = {
                            action: CTATypes.OWN_ANCHOR,
                            ownAnchor: parentAnchors ? parentAnchors[0].id : null
                          };
                      } else {
                        newEvents[target.item.positionParent[0]]
                          .contents[target.item.positionParent[1]]
                          .endOfContent = source.item.linkTo;
                      }
                      // Remove follow node in Target event
                      if (target.itemType === CTA_OTHER_CONTENT) {
                        const { contents: targetContents } = events[indexTargetEvent];
                        const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
                        const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
                        for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
                        }
                      }
                      break;
                  }
                  break;
                }
                // Source: nodeType = NODE_CTA
                default:
                  switch (source.itemType) {
                    // Source: itemType = CTA_OTHER_CONTENT
                    case CTA_OTHER_CONTENT:
                      newEvents[target.item.positionParent[0]]
                        .contents[target.item.positionParent[1]]
                        .endOfContent = {
                          action: CTATypes.OTHER_CONTENT,
                          content: {
                            id: source.item.id,
                            anchor:
                              newEvents[source.item.positionParent[0]]
                                .contents[source.item.positionParent[1]]
                                .ctas[source.item.positionParent[2]]
                                .linkTo.content.anchor || ''
                          }
                        };
                      newEvents[source.item.positionParent[0]]
                        .contents[source.item.positionParent[1]]
                        .ctas[source.item.positionParent[2]]
                        .linkTo = null;
                      newEvents[source.item.positionNode[0]]
                        .contents[source.item.positionNode[1]]
                        .availableAt = 'afterContent';
                      // Check if drop to difference event
                      if (indexSourceEvent !== indexTargetEvent) {
                        const linkedContents = [];
                        const { contents } = newEvents[indexSourceEvent];
                        // Find all contents follow
                        const arrIndex = getRemoveIndex(source.item.id, contents);
                        // Push content to linkedContents
                        arrIndex.map(index => linkedContents.push(contents[index]));
                        // Set 'availableAtDetails' ID
                        linkedContents[0].availableAtDetails.id =
                          newEvents[target.item.positionParent[0]]
                            .contents[target.item.positionParent[1]].id;

                        newEvents[indexTargetEvent].contents.push(...linkedContents);
                        // Remove source follow node
                        const arrIndexSorted = arrIndex.sort((a, b) => a - b);
                        for (let i = arrIndexSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexSourceEvent].contents.splice(arrIndexSorted[i], 1);
                        }
                      }
                      // Remove follow node in Target event
                      if (target.itemType === CTA_OTHER_CONTENT) {
                        const { contents: targetContents } = events[indexTargetEvent];
                        const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
                        const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
                        for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
                        }
                      }
                      break;
                    // Source: itemType = CTA
                    default:
                      newEvents[source.item.positionNode[0]]
                        .contents[source.item.positionNode[1]]
                        .ctas[source.item.positionNode[2]]
                        .linkTo = null;
                      if (source.itemType === CTA_OWN_ANCHOR) {
                        newEvents[target.item.positionParent[0]]
                          .contents[target.item.positionParent[1]]
                          .endOfContent = {
                            action: CTATypes.OWN_ANCHOR,
                            ownAnchor: parentAnchors ? parentAnchors[0].id : null
                          };
                      } else {
                        newEvents[target.item.positionParent[0]]
                          .contents[target.item.positionParent[1]]
                          .endOfContent = source.item.linkTo;
                      }
                      // Remove follow node in Target event
                      if (target.itemType === CTA_OTHER_CONTENT) {
                        const { contents: targetContents } = events[indexTargetEvent];
                        const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
                        const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
                        for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
                        }
                      }
                      break;
                  }
                  break;
              }
              break;
            }
            // Target: nodeType = NODE_CTA
            case NODE_CTA: {
              switch (source.nodeType) {
                // Source: nodeType = NODE_INIT
                case NODE_INIT:
                  // Todo: It should be removed when handling disable node successed
                  if (indexSourceEvent !== indexTargetEvent) {
                    const sourceContents = events[indexSourceEvent].contents;
                    // Set 'availableAtDetails' ID
                    sourceContents[0].availableAtDetails.id =
                      newEvents[target.item.positionParent[0]]
                        .contents[target.item.positionParent[1]].id;
                    // Set 'afterCTA' for the first content
                    sourceContents[0].availableAt = 'afterCTA';
                    // Add sourceContents to Target event
                    newEvents[indexTargetEvent].contents.push(...sourceContents);
                    // Set linkTo value in Target node
                    if (target.itemType === CTA_OTHER_CONTENT) {
                      newEvents[target.item.positionParent[0]]
                        .contents[target.item.positionParent[1]]
                        .ctas[target.item.positionParent[2]]
                        .linkTo = {
                          action: CTATypes.OTHER_CONTENT,
                          content: {
                            id: source.item.id,
                            anchor: sourceAnchors ? sourceAnchors[0].id : null
                          }
                        };
                    } else {
                      newEvents[target.item.positionNode[0]]
                        .contents[target.item.positionNode[1]]
                        .ctas[target.item.positionNode[2]]
                        .linkTo = {
                          action: CTATypes.OTHER_CONTENT,
                          content: {
                            id: source.item.id,
                            anchor: sourceAnchors ? sourceAnchors[0].id : null
                          }
                        };
                    }
                    // Remove all content in Source event
                    newEvents[indexSourceEvent].contents = [];
                    // Remove follow node in Target event
                    if (target.itemType === CTA_OTHER_CONTENT) {
                      const { contents: targetContents } = events[indexTargetEvent];
                      const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
                      const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
                      for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
                        newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
                      }
                    }
                  }
                  break;
                // Source: nodeType = NODE_END_OF_CONTENT
                case NODE_END_OF_CONTENT: {
                  switch (source.itemType) {
                    // Source: itemType = CTA_OTHER_CONTENT
                    case CTA_OTHER_CONTENT: {
                      const currentAnchor = newEvents[source.item.positionParent[0]]
                        .contents[source.item.positionParent[1]]
                        .endOfContent.content.anchor || '';
                      // Set source's endOfContent is empty
                      newEvents[source.item.positionParent[0]]
                        .contents[source.item.positionParent[1]]
                        .endOfContent = null;
                      // Set 'afterCTA'
                      newEvents[source.item.positionNode[0]]
                        .contents[source.item.positionNode[1]]
                        .availableAt = 'afterCTA';
                      // Check if drop to difference event
                      if (indexSourceEvent !== indexTargetEvent) {
                        const linkedContents = [];
                        const { contents: sourceContents } = newEvents[indexSourceEvent];
                        const arrIndexSource = getRemoveIndex(source.item.id, sourceContents);
                        // Get linkedContents
                        arrIndexSource.map(index => linkedContents.push(sourceContents[index]));
                        // Set 'availableAtDetails' ID
                        linkedContents[0].availableAtDetails.id =
                          newEvents[target.item.positionParent[0]]
                            .contents[target.item.positionParent[1]].id;
                        // Add follow node to Target event
                        newEvents[indexTargetEvent].contents.push(...linkedContents);
                        // Remove source follow node
                        const arrIndexSourceSorted = arrIndexSource.sort((a, b) => a - b);
                        for (let i = arrIndexSourceSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexSourceEvent].contents.splice(arrIndexSourceSorted[i], 1);
                        }
                      }
                      // Set linkTo
                      if (target.itemType === CTA_OTHER_CONTENT) {
                        newEvents[target.item.positionParent[0]]
                          .contents[target.item.positionParent[1]]
                          .ctas[target.item.positionParent[2]]
                          .linkTo = {
                            action: CTATypes.OTHER_CONTENT,
                            content: {
                              id: source.item.id,
                              anchor: currentAnchor
                            }
                          };
                        // Remove follow node in Target event
                        const { contents: targetContents } = newEvents[indexTargetEvent];
                        const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
                        const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
                        for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
                        }
                      } else {
                        newEvents[target.item.positionNode[0]]
                          .contents[target.item.positionNode[1]]
                          .ctas[target.item.positionNode[2]]
                          .linkTo = {
                            action: CTATypes.OTHER_CONTENT,
                            content: {
                              id: source.item.id,
                              anchor: currentAnchor
                            }
                          };
                      }
                      break;
                    }
                    // Source: itemType = CTA
                    default: {
                      newEvents[source.item.positionParent[0]]
                        .contents[source.item.positionParent[1]]
                        .endOfContent = null;
                      // Check and reset CTA_OWN_ANCHOR
                      let linkTo;
                      if (source.itemType === CTA_OWN_ANCHOR) {
                        linkTo = {
                          action: CTATypes.OWN_ANCHOR,
                          ownAnchor: parentAnchors ? parentAnchors[0].id : null
                        };
                      } else {
                        linkTo = source.item.linkTo; // eslint-disable-line prefer-destructuring
                      }
                      // Add linkTo to Target
                      if (target.itemType === CTA_OTHER_CONTENT) {
                        newEvents[target.item.positionParent[0]]
                          .contents[target.item.positionParent[1]]
                          .ctas[target.item.positionParent[2]]
                          .linkTo = linkTo;
                      } else {
                        newEvents[target.item.positionNode[0]]
                          .contents[target.item.positionNode[1]]
                          .ctas[target.item.positionNode[2]]
                          .linkTo = linkTo;
                      }
                      // Remove follow node in Target event
                      if (target.itemType === CTA_OTHER_CONTENT) {
                        const { contents: targetContents } = events[indexTargetEvent];
                        const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
                        const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
                        for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
                        }
                      }
                      break;
                    }
                  }
                  break;
                }
                // Source: nodeType = NODE_CTA
                default: {
                  switch (source.itemType) {
                    // Source: itemType = CTA_OTHER_CONTENT
                    case CTA_OTHER_CONTENT: {
                      const currentAnchor = newEvents[source.item.positionParent[0]]
                        .contents[source.item.positionParent[1]]
                        .ctas[source.item.positionParent[2]]
                        .linkTo.content.anchor || '';
                      // Set source's endOfContent is empty
                      newEvents[source.item.positionParent[0]]
                        .contents[source.item.positionParent[1]]
                        .ctas[source.item.positionParent[2]]
                        .linkTo = null;
                      // Set 'afterCTA'
                      newEvents[source.item.positionNode[0]]
                        .contents[source.item.positionNode[1]]
                        .availableAt = 'afterCTA';
                      // Check if drop to difference event
                      if (indexSourceEvent !== indexTargetEvent) {
                        const linkedContents = [];
                        const { contents: sourceContents } = newEvents[indexSourceEvent];
                        const arrIndexSource = getRemoveIndex(source.item.id, sourceContents);
                        // Get linkedContents
                        arrIndexSource.map(index => linkedContents.push(sourceContents[index]));
                        // Set 'availableAtDetails' ID
                        linkedContents[0].availableAtDetails.id =
                          newEvents[target.item.positionParent[0]]
                            .contents[target.item.positionParent[1]].id;
                        // Add follow node to Target event
                        newEvents[indexTargetEvent].contents.push(...linkedContents);
                        // Remove source follow node
                        const arrIndexSourceSorted = arrIndexSource.sort((a, b) => a - b);
                        for (let i = arrIndexSourceSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexSourceEvent].contents.splice(arrIndexSourceSorted[i], 1);
                        }
                      }
                      // Set linkTo
                      if (target.itemType === CTA_OTHER_CONTENT) {
                        newEvents[target.item.positionParent[0]]
                          .contents[target.item.positionParent[1]]
                          .ctas[target.item.positionParent[2]]
                          .linkTo = {
                            action: CTATypes.OTHER_CONTENT,
                            content: {
                              id: source.item.id,
                              anchor: currentAnchor
                            }
                          };
                        // Remove follow node in Target event
                        const { contents: targetContents } = newEvents[indexTargetEvent];
                        const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
                        const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
                        for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
                        }
                      } else {
                        newEvents[target.item.positionNode[0]]
                          .contents[target.item.positionNode[1]]
                          .ctas[target.item.positionNode[2]]
                          .linkTo = {
                            action: CTATypes.OTHER_CONTENT,
                            content: {
                              id: source.item.id,
                              anchor: currentAnchor
                            }
                          };
                      }
                      break;
                    }
                    // Source: itemType = CTA
                    default: {
                      newEvents[source.item.positionNode[0]]
                        .contents[source.item.positionNode[1]]
                        .ctas[source.item.positionNode[2]]
                        .linkTo = null;
                      // Check and reset CTA_OWN_ANCHOR
                      let linkTo;
                      if (source.itemType === CTA_OWN_ANCHOR) {
                        linkTo = {
                          action: CTATypes.OWN_ANCHOR,
                          ownAnchor: parentAnchors ? parentAnchors[0].id : null
                        };
                      } else {
                        linkTo = source.item.linkTo; // eslint-disable-line prefer-destructuring
                      }
                      // Add linkTo to Target
                      if (target.itemType === CTA_OTHER_CONTENT) {
                        newEvents[target.item.positionParent[0]]
                          .contents[target.item.positionParent[1]]
                          .ctas[target.item.positionParent[2]]
                          .linkTo = linkTo;
                      } else {
                        newEvents[target.item.positionNode[0]]
                          .contents[target.item.positionNode[1]]
                          .ctas[target.item.positionNode[2]]
                          .linkTo = linkTo;
                      }
                      // Remove follow node in Target event
                      if (target.itemType === CTA_OTHER_CONTENT) {
                        const { contents: targetContents } = events[indexTargetEvent];
                        const arrIndexTarget = getRemoveIndex(target.item.id, targetContents);
                        const arrIndexTargetSorted = arrIndexTarget.sort((a, b) => a - b);
                        for (let i = arrIndexTargetSorted.length - 1; i >= 0; i -= 1) {
                          newEvents[indexTargetEvent].contents.splice(arrIndexTargetSorted[i], 1);
                        }
                      }
                      break;
                    }
                  }
                  break;
                }
              }
              break;
            }
            default:
              break;
          }
          break;
        }
      }
    } else {
      throw new Error('This content already exists');
    }

    // Reset currentNodeSelected
    yield put({ type: RESET_CURRENT_NODE_SELECTED });

    // Handle select
    if (source.itemType === CTA_OTHER_CONTENT) {
      // Node init (p -> p)
      // Node cta
      //   + Empty (n -> p)
      //   + CTA (n -> p)
      //   + Content (p -> p)
      // Node eoc
      //   + CTA (p -> p)
      //   + Content (p -> p)
      if (!window.sqoonyNodeDroppedId) {
        window.sqoonyNodeDroppedId = source.item.id;
      }
      if (target.nodeType === NODE_CTA && target.itemType !== CTA_OTHER_CONTENT) {
        window.sqoonyNodeSelected = {
          // positionNode is updated in UPDATE_WORKSPACE_DATA
          positionParent: target.item.positionNode
        };
      } else {
        window.sqoonyNodeSelected = {
          // positionNode is updated in UPDATE_WORKSPACE_DATA
          positionParent: target.item.positionParent
        };
      }
    } else {
      // Node cta
      //   + CTA (n -> n | p -> p)
      //   + Content (p -> n | [p1, p2] -> p)
      // Node eoc
      //   + CTA (n/p -> n | p -> p)
      //   + Content (p -> n | p -> p)
      if (target.itemType === CTA_OTHER_CONTENT) { // eslint-disable-line
        window.sqoonyNodeSelected = {
          positionNode: target.item.positionParent,
          positionParent: [target.item.positionParent[0], target.item.positionParent[1]]
        };
      } else {
        window.sqoonyNodeSelected = {
          positionNode: target.item.positionNode || target.item.positionParent,
          positionParent: target.item.positionParent
        };
      }
    }

    // Put SUCCESS
    yield put({
      type: HANDLE_DROP_SUCCESS,
      newEvents
    });

    // Update workspace data
    yield put({ type: UPDATE_WORKSPACE_DATA });

    // Save work state to History
    yield put({ type: SAVE_WORK_STATE });
    // Call api save data
    yield saveWorkspace();
  } catch (e) {
    console.log('Error:', e.message);
    yield put({
      type: HANDLE_DROP_FAILURE,
      appError: {
        status: e.response ? e.response.status : null,
        statusText: '',
        data: {
          message: e.message
        }
      }
    });
  }
}

/**
 * Undo work state
 * @param action
 */
function* undoState(action) {
  try {
    // Get program state
    const state = yield select(programState);
    const currentEvents = state.getIn(['workspaceData', 'events']).toJS();
    const currentStateIndex = state.getIn(['workspaceHistory', 'currentIndex']);
    const nextEvents = state.getIn(['workspaceHistory', 'datas', currentStateIndex - 2, 'events']).toJS();

    let eventResponse;
    let diff;

    // === : Check name to Update event
    //  >  : Handle undo after create event
    //  <  : Handle undo after delete event
    if (currentEvents.length === nextEvents.length) {
      for (let i = 0; i < currentEvents.length; i += 1) {
        if (currentEvents[i].name !== nextEvents[i].name) {
          switch (window.sqoonyEditorMode) {
            case PROGRAM_MODE:
              yield updateEvent(
                action.payload.token, nextEvents[i].id, nextEvents[i].name
              );
              break;
            case BROADCAST_MODE:
            case AUTOMATED_MODE:
              break;
            default:
              break;
          }
          break;
        }
      }
    } else if (currentEvents.length > nextEvents.length) {
      // Get different event between "nextEvents" and "currentEvents"
      diff = currentEvents.filter(
        eventX => nextEvents.filter(
          eventY => eventY.name === eventX.name
        ).length === 0
      );
      // Delete event
      eventResponse = yield deleteEvent(action.payload.token, diff[0].id);
    } else {
      // Get different event between "nextEvents" and "currentEvents"
      diff = nextEvents.filter(
        eventX => currentEvents.filter(
          eventY => eventY.name === eventX.name
        ).length === 0
      );
      // Create new event
      eventResponse = yield createEvent(action.payload.token, diff[0].name);
    }

    // Put Undo success
    yield put({
      type: UNDO_WORK_STATE,
      payload: {
        id: eventResponse && eventResponse.id,
        positionNode: diff && diff[0].positionNode
      }
    });
    // Call api save data
    yield saveWorkspace();
  } catch (e) {
    console.log('Error', e.response);
    yield put({
      type: UPDATE_EVENT_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Redo work state
 * @param action
 */
function* redoState(action) {
  try {
    // Get program state
    const state = yield select(programState);
    const currentEvents = state.getIn(['workspaceData', 'events']).toJS();
    const currentStateIndex = state.getIn(['workspaceHistory', 'currentIndex']);
    const nextEvents = state.getIn(['workspaceHistory', 'datas', currentStateIndex, 'events']).toJS();

    let eventResponse;
    let diff;

    // === : Check name to Update event
    //  <  : Handle redo after create event
    //  >  : Handle redo after delete event
    if (currentEvents.length === nextEvents.length) {
      for (let i = 0; i < currentEvents.length; i += 1) {
        if (currentEvents[i].name !== nextEvents[i].name) {
          switch (window.sqoonyEditorMode) {
            case PROGRAM_MODE:
              yield updateEvent(
                action.payload.token, nextEvents[i].id, nextEvents[i].name
              );
              break;
            case BROADCAST_MODE:
            case AUTOMATED_MODE:
              break;
            default:
              break;
          }
          break;
        }
      }
    } else if (currentEvents.length < nextEvents.length) {
      // Get different event between "nextEvents" and "currentEvents"
      diff = nextEvents.filter(
        eventX => currentEvents.filter(
          eventY => eventY.name === eventX.name
        ).length === 0
      );
      // Create new event
      eventResponse = yield createEvent(action.payload.token, diff[0].name);
    } else {
      // Get different event between "nextEvents" and "currentEvents"
      diff = currentEvents.filter(
        eventX => nextEvents.filter(
          eventY => eventY.name === eventX.name
        ).length === 0
      );
      // Delete event
      eventResponse = yield deleteEvent(action.payload.token, diff[0].id);
    }

    // Put Undo success
    yield put({
      type: REDO_WORK_STATE,
      payload: {
        id: eventResponse && eventResponse.id,
        positionNode: diff && diff[0].positionNode
      }
    });
    // Call api save data
    yield saveWorkspace();
  } catch (e) {
    console.log('Error', e.response);
    yield put({
      type: UPDATE_EVENT_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Search content library
 * @param action
 */
function* searchContentLibrary(action) {
  try {
    const searchResult = yield searchContent(
      localStorage.getItem('token'),
      action.payload.keyword,
      action.payload.page,
      action.payload.size
    );
    yield put({
      type: SEARCH_CONTENT_SUCCESS,
      searchResult
    });
  } catch (e) {
    console.log(e.response);
    yield put({
      type: SEARCH_CONTENT_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Update content detail
 * @param action
 */
function* updateContentNew(action) {
  try {
    const state = yield select(programState);
    const currentEvents = state.getIn(['workspaceData', 'events']).toJS();
    // Get latest content detail
    let latestContent = yield getContentDetail(action.payload.item.contentId, action.payload.token);
    if (latestContent.status !== 200) {
      throw new Error('We can\'t find the content. Did someone delete it? Please try again.');
    } else {
      latestContent = {
        ...latestContent.data,
        id: null,
        contentId: latestContent.data.id
      };
    }
    const newEvents = currentEvents.map((event) => {
      const newContents = [...event.contents];
      event.contents.forEach((oldContent) => {
        if (oldContent.contentId !== latestContent.contentId) { return; }
        // Get list CTA was deleted
        const listCtaDeleted = oldContent.ctas
          .filter(
            oldCta => latestContent.ctas.filter(
              latestCta => latestCta.id === oldCta.id
            ).length === 0
          );

        // Delete contents follow CtaDeleted
        listCtaDeleted.forEach((cta) => {
          // Delete CTA_OTHER_CONTENT only
          if (cta.linkTo && cta.linkTo.action && cta.linkTo.action === CTATypes.OTHER_CONTENT) {
            // Get array index of contents need to delete
            const arrIndex = getRemoveIndex(
              cta.linkTo.content.id, newContents
            ).sort((a, b) => a - b);
            // Remove content node
            for (let i = arrIndex.length - 1; i >= 0; i -= 1) {
              newContents.splice(arrIndex[i], 1);
            }
          }
        });

        // Delete node CTA
        const oldContentCtas = oldContent.ctas.filter(
          cta => listCtaDeleted.filter(
            ctaDeleted => ctaDeleted.id === cta.id
          ).length === 0
        );

        // Merge CTA
        const ctaMerged = latestContent.ctas.map(
          (latestCta) => {
            const result = oldContentCtas.find(oldCta => oldCta.id === latestCta.id);
            if (result) {
              return {
                ...result,
                ...latestCta
              };
            }
            return {
              ...latestCta
            };
          }
        );

        // Merge content
        const contentMerged = {
          ...oldContent,
          ...latestContent,
          id: oldContent.id,
          ctas: ctaMerged
        };

        // Get index of content in new array content
        const contentIndex = newContents.findIndex(content => content.id === oldContent.id);
        if (contentIndex < 0) { return; }

        // Set new content and updateStatus
        newContents[contentIndex] = {
          ...contentMerged,
          updateStatus: UP_TO_DATE
        };
      });
      return {
        ...event,
        contents: newContents
      };
    });
    // Reset currentNodeSelected
    yield put({ type: RESET_CURRENT_NODE_SELECTED });
    // Put Update successed
    yield put({
      type: UPDATE_CONTENT_SUCCESS,
      newEvents
    });
    yield put({ type: UPDATE_WORKSPACE_DATA });
    yield put({ type: SAVE_WORK_STATE });
    // Call api save data
    yield saveWorkspace();
  } catch (e) {
    console.log(e.response || e);
    yield put({
      type: UPDATE_CONTENT_FAILURE,
      appError: {
        status: e.response ? e.response.status : '500',
        statusText: '',
        data: {
          message: e.message
        }
      }
    });
  }
}

/**
 * Update content detail
 * @param action
 */
// function* updateContent(action) {
//   try {
//     const state = yield select(programState);
//     const currentContents = state.getIn(
//       ['workspaceData', 'events', action.payload.item.positionNode[0], 'contents']
//     ).toJS();
//     const newContents = [...currentContents];
//     // Get old content
//     const oldContent = newContents[action.payload.item.positionNode[1]];
//     // Get latest content detail
//     let latestContent = yield getContentDetail(action.payload.item.contentId, action.payload.token);
//     if (latestContent.status !== 200) {
//       throw new Error('We can\'t find the content. Did someone delete it? Please try again.');
//     } else {
//       latestContent = {
//         ...latestContent.data,
//         id: action.payload.item.id,
//         contentId: action.payload.item.contentId
//       };
//     }
//
//     // Get list CTA was deleted
//     const listCtaDeleted = oldContent.ctas
//       .filter(
//         oldCta => latestContent.ctas.filter(
//           latestCta => latestCta.id === oldCta.id
//         ).length === 0
//       );
//
//     // Delete contents follow CtaDeleted
//     listCtaDeleted.forEach((cta) => {
//       // Delete CTA_OTHER_CONTENT only
//       if (cta.linkTo && cta.linkTo.action && cta.linkTo.action === CTATypes.OTHER_CONTENT) {
//         // Get array index of contents need to delete
//         const arrIndex = getRemoveIndex(
//           cta.linkTo.content.id, newContents
//         ).sort((a, b) => a - b);
//         // Remove content node
//         for (let i = arrIndex.length - 1; i >= 0; i -= 1) {
//           newContents.splice(arrIndex[i], 1);
//         }
//       }
//     });
//
//     // Delete node CTA
//     const oldContentCtas = oldContent.ctas.filter(
//       cta => listCtaDeleted.filter(
//         ctaDeleted => ctaDeleted.id === cta.id
//       ).length === 0
//     );
//
//     // Merge CTA
//     const ctaMerged = latestContent.ctas.map(
//       (latestCta) => {
//         const result = oldContentCtas.find(oldCta => oldCta.id === latestCta.id);
//         if (result) {
//           return {
//             ...result,
//             ...latestCta
//           };
//         }
//         return {
//           ...latestCta
//         };
//       }
//     );
//
//     // Merge content
//     const contentMerged = {
//       ...oldContent,
//       ...latestContent,
//       ctas: ctaMerged
//     };
//
//     // Get index of content in new array content
//     const contentIndex = newContents.findIndex(content => content.id === action.payload.item.id);
//
//     // Set new content and updateStatus
//     newContents[contentIndex] = {
//       ...contentMerged,
//       updateStatus: UP_TO_DATE
//     };
//
//     yield put({
//       type: UPDATE_CONTENT_SUCCESS,
//       payload: {
//         newContents,
//         eventIndex: action.payload.item.positionNode[0]
//       }
//     });
//
//     yield put({ type: UPDATE_WORKSPACE_DATA });
//     yield put({ type: SAVE_WORK_STATE });
//     // Call api save data
//     yield saveWorkspace();
//   } catch (e) {
//     console.log(e.response || e);
//     yield put({
//       type: UPDATE_CONTENT_FAILURE,
//       appError: {
//         status: e.response ? e.response.status : '500',
//         statusText: '',
//         data: {
//           message: e.message
//         }
//       }
//     });
//   }
// }

/**
 * Fetch desktop player data
 * @param action
 */
function* fetchDesktopPlayerData(action) {
  try {
    const token = window.mainApp.tenantSecret;
    const desktopPlayerData = yield getDesktopPlayerData(
      action.payload.type,
      action.payload.journeyId,
      token,
      action.payload.eventId
    );
    yield put({
      type: FETCH_DESKTOP_PLAYER_SUCCESS,
      desktopPlayerData
    });
  } catch (e) {
    console.log(e.response || e);
    yield put({
      type: FETCH_DESKTOP_PLAYER_FAILURE,
      appError: {
        status: e.response ? e.response.status : null,
        statusText: e.response ? e.response.statusText : '',
        data: {
          message: e.response ? e.response.data.message : e.message
        }
      }
    });
  }
}

/**
 * Capture change properties (context box) and autosave
 */
function* captureChangeProperties() {
  try {
    yield delay(1000);
    yield saveWorkspace();
  } catch (e) {
    console.log(e.response || e);
    yield put({
      type: SAVE_PROGRAM_FAILURE,
      appError: {
        status: e.response ? e.response.status : null,
        statusText: e.response ? e.response.statusText : '',
        data: {
          message: e.response ? e.response.data.message : e.message
        }
      }
    });
  }
}

/**
 * Fetch list audiences
 * @param action
 */
function* fetchAudiences(action) {
  try {
    const audiences = yield getAudiences(action.payload.token);
    yield put({
      type: FETCH_AUDIENCES_SUCCESS,
      audiences
    });
  } catch (e) {
    console.log(e.response);
    yield put({
      type: FETCH_AUDIENCES_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Fetch list automated broadcast
 * @param action
 */
function* fetchAutomatedBroadcast(action) {
  try {
    const automatedBroadcasts = yield getAutomatedBroadcasts(action.payload.token);
    yield put({
      type: FETCH_AUTOMATED_BROADCAST_SUCCESS,
      automatedBroadcasts
    });
  } catch (e) {
    console.log(e.response);
    yield put({
      type: FETCH_AUTOMATED_BROADCAST_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

/**
 * Fetch tenant settings
 * @param action
 */
function* fetchTenantSettings(action) {
  try {
    const tenantSettings = yield getTenantSettings(action.payload.token);
    yield put({
      type: FETCH_TENANT_SETTINGS_SUCCESS,
      tenantSettings
    });
  } catch (e) {
    console.log(e.response);
    yield put({
      type: FETCH_TENANT_SETTINGS_FAILURE,
      appError: {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      }
    });
  }
}

function* programSaga() {
  yield takeLatest(FETCH_PROGRAM_DATA_REQUEST, fetchProgramData);
  yield takeLatest(FETCH_BROADCAST_DATA_REQUEST, fetchBroadcastData);
  yield takeLatest(FETCH_CONTENT_LIBRARY_REQUEST, fetchContentLibrary);
  yield takeLatest(SAVE_PROGRAM_REQUEST, saveProgramData);
  yield takeLatest(SAVE_BROADCAST_REQUEST, saveBroadcastData);
  yield takeLatest(FETCH_EVENT_DATA_REQUEST, fetchEventData);
  yield takeLatest(CREATE_EVENT_REQUEST, createNewEvent);
  yield takeLatest(UPDATE_EVENT_REQUEST, updateEventData);
  yield takeLatest(DELETE_EVENT_REQUEST, deleteEventData);
  yield takeLatest(HANDLE_REMOVE_REQUEST, handleRemoveNode);
  yield takeLatest(HANDLE_DROP_REQUEST, handleDrop);
  yield takeLatest(UNDO_REQUEST, undoState);
  yield takeLatest(REDO_REQUEST, redoState);
  yield takeLatest(SEARCH_CONTENT_REQUEST, searchContentLibrary);
  yield takeLatest(UPDATE_CONTENT_REQUEST, updateContentNew);
  yield takeLatest(FETCH_DESKTOP_PLAYER_REQUEST, fetchDesktopPlayerData);
  yield takeLatest(HANDLE_CHANGE_PROPERTIES, captureChangeProperties);
  yield takeLatest(FETCH_AUDIENCES_REQUEST, fetchAudiences);
  yield takeLatest(FETCH_AUTOMATED_BROADCAST_REQUEST, fetchAutomatedBroadcast);
  yield takeLatest(FETCH_TENANT_SETTINGS_REQUEST, fetchTenantSettings);
}

export default programSaga;



// WEBPACK FOOTER //
// ./src/containers/program/saga.js